/**
=========================================================
* Material Dashboard 2 PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { FC, ReactNode, forwardRef } from "react";

// @mui material components
import { ButtonProps } from "@mui/material";

// Custom styles for MDButton
import MDButtonRoot from "components/MDButton/MDButtonRoot";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController } from "context";

// Declaring props types for MDButton
interface Props extends Omit<ButtonProps, "color" | "variant"> {
  color?:
    | "white"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "default"
    | "darkGreen"
    | "blue";
  variant?: "text" | "contained" | "outlined" | "gradient";
  size?: "small" | "medium" | "large";
  circular?: boolean;
  iconOnly?: boolean;
  children?: ReactNode;
  [key: string]: any;
}

const MDButton: FC<Props> = forwardRef(
  ({ color, variant, size, circular, iconOnly, children, ...rest }, ref) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return (
      <MDButtonRoot
        {...rest}
        ref={ref}
        color="primary"
        variant={variant === "gradient" ? "contained" : variant}
        size={size}
        ownerState={{ color, variant, size, circular, iconOnly, darkMode }}
        sx={{
          ...(color === "success" &&
            variant !== "outlined" && {
              backgroundColor: "#15D11C",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#13BA19",
              },
            }),
          ...(color === "success" &&
            variant === "outlined" && {
              borderColor: "#15D11C",
              color: "#15D11C",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "rgba(21,209,28,0.1)", // mierne zafarbené pozadie pri hover
              },
            }),
          ...(color === "warning" && {
            backgroundColor: "#F5A524",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#e89c1d", // uprav podľa potreby
            },
          }),
          ...(color === "error" &&
            variant !== "outlined" && {
              backgroundColor: "#DD2626",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#C01F1F", // upravená tmavšia verzia pre hover
              },
            }),
          ...(color === "error" &&
            variant === "outlined" && {
              borderColor: "#DD2626",
              color: "#DD2626",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "rgba(221,38,38,0.1)",
              },
            }),
            ...(color === "darkGreen" &&
              variant !== "outlined" && {
                backgroundColor: "#309B0A",
                color: "#fff",
                boxShadow: "0rem 0.1875rem 0.1875rem 0rem rgba(48,155,10,0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(48,155,10,0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(48,155,10,0.15)",
                transition: "background-color 0.3s, box-shadow 0.3s",
                "&:hover": {
                  backgroundColor: "#278c08", // Prispôsob tmavšia verzia podľa potreby
                  boxShadow: "0rem 0.875rem 1.625rem -0.75rem rgba(48,155,10,0.4), 0rem 0.25rem 1.4375rem 0rem rgba(48,155,10,0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(48,155,10,0.2)",
                  color: "#fff",
                },
              }),
            ...(color === "darkGreen" &&
              variant === "outlined" && {
                borderColor: "#309B0A",
                color: "#309B0A",
                backgroundColor: "transparent",
                transition: "background-color 0.3s, box-shadow 0.3s",
                "&:hover": {
                  backgroundColor: "rgba(48,155,10,0.1)",
                  boxShadow: "0rem 0.875rem 1.625rem -0.75rem rgba(48,155,10,0.4), 0rem 0.25rem 1.4375rem 0rem rgba(48,155,10,0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(48,155,10,0.2)",
                },
              }),
              ...(color === "blue" &&
                variant !== "outlined" && {
                  backgroundColor: "#006FEE",
                  color: "#fff",
                  boxShadow: "0rem 0.1875rem 0.1875rem 0rem rgba(0,111,238,0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(0,111,238,0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(0,111,238,0.15)",
                  transition: "background-color 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    backgroundColor: "#0056c7", // upravená tmavšia verzia pre hover
                    boxShadow: "0rem 0.875rem 1.625rem -0.75rem rgba(0,111,238,0.4), 0rem 0.25rem 1.4375rem 0rem rgba(0,111,238,0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(0,111,238,0.2)",
                  },
                }),
              ...(color === "blue" &&
                variant === "outlined" && {
                  borderColor: "#006FEE",
                  color: "#006FEE",
                  backgroundColor: "transparent",
                  transition: "background-color 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    backgroundColor: "rgba(0,111,238,0.1)",
                    boxShadow: "0rem 0.875rem 1.625rem -0.75rem rgba(0,111,238,0.4), 0rem 0.25rem 1.4375rem 0rem rgba(0,111,238,0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(0,111,238,0.2)",
                  },
                }),
        }}
      >
        {children}
      </MDButtonRoot>
    );
  }
);

// Declaring default props for MDButton
MDButton.defaultProps = {
  color: "white",
  variant: "contained",
  size: "medium",
  circular: false,
  iconOnly: false,
};

export default MDButton;
